/**
 * Role-based routing utilities
 * Provides centralized role-to-dashboard mapping for consistent routing
 */

import type { Role } from '../types/auth';

/**
 * Dashboard routes for each role
 */
export const ROLE_DASHBOARDS: Partial<Record<Role, string>> = {
  SUPER_ADMIN: '/admin',
  ADMIN: '/admin',
  CAREGIVER: '/caregiver',
  FAMILY: '/family-portal',
};

/**
 * Get the appropriate dashboard route for a user based on their roles
 * Uses priority-based role detection
 */
export function getDashboardRoute(roles: Role[]): string {
  if (roles.includes('SUPER_ADMIN')) return '/admin';
  if (roles.includes('ADMIN')) return '/admin';
  if (roles.includes('CAREGIVER')) return '/caregiver';
  if (roles.includes('FAMILY')) return '/family-portal';
  return '/';
}

/**
 * Check if a user should be redirected based on their role and current path
 */
export function shouldRedirect(roles: Role[], currentPath: string): boolean {
  if (currentPath === '/login') {
    return false;
  }
  if (roles.includes('SUPER_ADMIN')) return false;
  if (roles.includes('ADMIN')) {
    return currentPath.startsWith('/enterprise-readiness') ||
      currentPath.startsWith('/gp-connect') ||
      currentPath.startsWith('/family-portal') ||
      currentPath.startsWith('/caregiver');
  }
  if (roles.includes('CAREGIVER')) {
    return !currentPath.startsWith('/caregiver') &&
      !currentPath.startsWith('/tasks') &&
      !currentPath.startsWith('/time-tracking') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('FAMILY')) {
    return !currentPath.startsWith('/family-portal');
  }
  return true;
}

/**
 * Get a user-friendly role label
 */
export function getRoleLabel(role: Role): string {
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'CAREGIVER') return 'Caregiver';
  if (role === 'FAMILY') return 'Family Member';
  return 'Super Administrator';
}

/**
 * Get the primary role to display for a user
 * Returns the highest priority role
 */
export function getPrimaryRole(roles: Role[]): Role | null {
  if (roles.includes('SUPER_ADMIN')) return 'SUPER_ADMIN';
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('CAREGIVER')) return 'CAREGIVER';
  if (roles.includes('FAMILY')) return 'FAMILY';
  return null;
}
